import clsx from 'clsx';
import React, {useCallback, useMemo} from 'react';
import CircleIcon from '@mui/icons-material/Circle';
import {Body, Button} from '@lightricks/react-design-system';
import {formattedDate} from '@/utils/formattedDate';
import translate from '@/utils/translate';
import {MONTH_SHORT_DAY_YEAR_FORMAT} from '@/config/timeFormats';
import {type CreatorBrandSafetyVettingStatus} from '@/types/creatorBrandSafetyVetting';
import DropdownButton from '@/components/dropdown-button';
import {Option} from '@/components/dropdown-button/DropdownButton';
import styles from './VettingStatusChip.module.scss';

const DEFAULT_OPTIONS: Option[] = [
  {id: 'ready_for_review', label: translate('Ready for review')},
  {id: 'approved', label: translate('Approved')},
  {id: 'declined', label: translate('Declined')},
];

type VettingStatusChipProps = {
  status: CreatorBrandSafetyVettingStatus;
  decisionDate?: Date;
  readOnly?: boolean;
  onChange?: (option: {id: string}) => void;
  disabled?: boolean;
  options?: Option[];
  testID?: string;
  className?: string;
};

function VettingStatusChip({
  status,
  decisionDate,
  readOnly = false,
  onChange,
  disabled = false,
  options = DEFAULT_OPTIONS,
  testID = 'vetting-status-chip',
  className,
}: VettingStatusChipProps) {
  const label = useMemo(() => {
    return options.find((option) => option.id === status)?.label ?? '';
  }, [options, status]);

  const renderMenuItem = useCallback(
    (option: Option) => (
      <>
        <CircleIcon
          className={clsx(
            styles.circleIcon,
            styles[`circleIcon__${option.id}`]
          )}
        />
        {option.label}
      </>
    ),
    []
  );

  if (readOnly) {
    return (
      <Button
        data-testid={testID}
        disabled
        startIcon={<CircleIcon />}
        appearance="neutral"
        mode="plain"
        size="small"
        className={clsx(
          styles.button,
          styles.readOnly,
          styles[`button__${status}`],
          className
        )}
      >
        <Body size="md">
          <span className={styles.text}>
            <span className={styles.labelText}>{label}</span>
            {decisionDate && (
              <span className={styles.dateText}>
                {formattedDate(decisionDate, MONTH_SHORT_DAY_YEAR_FORMAT)}
              </span>
            )}
          </span>
        </Body>
      </Button>
    );
  }

  return (
    <DropdownButton
      testID={testID}
      disabled={disabled}
      startIcon={<CircleIcon />}
      buttonClassName={clsx(
        styles.button,
        styles[`button__${status}`],
        className
      )}
      renderMenuItem={renderMenuItem}
      menuProps={{
        classes: {paper: styles.menu},
        slotProps: {
          root: {
            slotProps: {
              backdrop: {
                invisible: true,
                onClick: (e) => {
                  e.stopPropagation();
                },
              },
            },
          },
        },
      }}
      menuItemProps={{classes: {root: styles.menuItem}}}
      label={label}
      options={options}
      onChange={(option) => onChange?.(option)}
    />
  );
}

export default VettingStatusChip;
